﻿import { StyleSheet, Platform } from 'react-native';
import type { ThemeMode } from '../../../src/theme/ThemeContext';
import type { Theme } from '../../../src/theme/getTheme';
import {
  WORLD_CUP_HEADER_DIVIDER_ON_BRAND,
  WORLD_CUP_HEADER_TRAIL_SLOT_PX,
  WORLD_CUP_HEADER_TRAIL_SLOT_NARROW_PX,
  WORLD_CUP_HEADER_TRAIL_GAP_NARROW,
  WORLD_CUP_HEADER_BUTTON_SIZE,
  WORLD_CUP_HEADER_BUTTON_SIZE_NARROW,
  WORLD_CUP_HEADER_TROPHY_SIZE,
  WORLD_CUP_HEADER_TROPHY_SIZE_NARROW,
  WORLD_CUP_HEADER_PADDING_H,
  WORLD_CUP_HEADER_PADDING_H_NARROW,
} from './WorldCupScreenHeader.constants';

export const getWorldCupScreenHeaderStyles = (theme: Theme, themeMode: ThemeMode, isNarrow = false) => {
  const btnSize = isNarrow ? WORLD_CUP_HEADER_BUTTON_SIZE_NARROW : WORLD_CUP_HEADER_BUTTON_SIZE;
  const trophySize = isNarrow ? WORLD_CUP_HEADER_TROPHY_SIZE_NARROW : WORLD_CUP_HEADER_TROPHY_SIZE;
  const paddingH = isNarrow ? WORLD_CUP_HEADER_PADDING_H_NARROW : WORLD_CUP_HEADER_PADDING_H;
  const trailSlot = isNarrow ? WORLD_CUP_HEADER_TRAIL_SLOT_NARROW_PX : WORLD_CUP_HEADER_TRAIL_SLOT_PX;

  return StyleSheet.create({
    screenHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: isNarrow ? 6 : 8,
      paddingHorizontal: paddingH,
      // Fits the compact 44pt XP cluster (LV pill overlaps the ring) with minimal growth
      // over the original bar.
      minHeight: isNarrow ? 54 : 58,
      overflow: 'visible',
      zIndex: 10,
      elevation: Platform.OS === 'android' ? 6 : 0,
      ...(Platform.OS === 'web' && {
        width: '100%' as never,
        maxWidth: '100%' as never,
        alignSelf: 'stretch' as never,
      }),
    },
    screenHeaderLeadFlex: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      flexShrink: 0,
      // Intrinsic width at every breakpoint: the XP cluster (ring + badge) is wider
      // than the old fixed lead slot, so let it size to content and center the title
      // in the space that remains.
      gap: isNarrow ? 4 : 8,
    },
    screenHeaderTitleCluster: {
      // Absolutely centered on the header so it stays screen-centered regardless of the
      // (now wider) XP-cluster lead slot; sits below the tappable side buttons and ignores
      // touches so the avatar/standings/menu stay pressable underneath.
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: isNarrow ? 4 : 6,
      paddingHorizontal: isNarrow ? 2 : 4,
      zIndex: 1,
      pointerEvents: 'none',
    },
    screenHeaderTrailFlex: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: isNarrow ? WORLD_CUP_HEADER_TRAIL_GAP_NARROW : 4,
      flexShrink: 0,
      zIndex: 3,
      ...(isNarrow ? {} : { minWidth: trailSlot }),
    },
    headerSideButton: {
      width: btnSize,
      height: btnSize,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: btnSize / 2,
      backgroundColor: 'transparent',
      zIndex: Platform.OS === 'web' ? 4 : 3,
      pointerEvents: 'box-only',
    },
    headerSideButtonPressed: {
      opacity: 0.65,
      ...(Platform.OS === 'web' && {
        backgroundColor: theme.gray[300],
        opacity: 1,
      }),
    },
    headerSideButtonHover: {
      backgroundColor: theme.gray[200],
    },
    screenHeaderIcon: {
      width: trophySize,
      height: trophySize,
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    screenHeaderDivider: {
      width: 2,
      height: isNarrow ? 14 : 18,
      borderRadius: 1,
      backgroundColor:
        themeMode === 'normal' ? WORLD_CUP_HEADER_DIVIDER_ON_BRAND : theme.gray[300],
      flexShrink: 0,
    },
    screenTitleText: {
      fontSize: isNarrow ? 13 : 15,
      fontWeight: '800',
      letterSpacing: isNarrow ? 0.5 : 1,
      color: themeMode === 'normal' ? theme.white : theme.gray[900],
      textTransform: 'uppercase',
      flexShrink: 1,
      minWidth: 0,
    },
    screenTitleYear: {
      fontSize: isNarrow ? 13 : 15,
      fontWeight: '800',
      letterSpacing: -0.3,
      color: themeMode === 'normal' ? theme.primaryLight : theme.primary,
      flexShrink: 0,
    },
  });
};
